import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import {
  getModulesAction,
  getTutorialByIdAction,
} from "../../Actions/KnowledgeBaseAction";
import ArticleFormDialog from "./ArticleFormDialog";
import "./Knowledgebase.css";

/**
 * Full-page Add / Edit Knowledge Base form.
 * Routes:
 *  - /knowledge-base/new
 *  - /knowledge-base/:moduleSlug/new
 *  - /knowledge-base/:moduleSlug/:articleId/edit
 */
const ArticleFormPage = () => {
  const navigate = useNavigate();
  const { moduleSlug, articleId } = useParams();
  const isEdit = Boolean(articleId);
  const fixedModuleSlug =
    moduleSlug && moduleSlug !== "new" && moduleSlug !== "all"
      ? moduleSlug
      : "";

  const [modules, setModules] = useState([]);
  const [tutorial, setTutorial] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");

  useEffect(() => {
    getModulesAction()
      .then((res) => setModules(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setTutorial(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    getTutorialByIdAction(articleId)
      .then((data) => {
        if (cancelled) return;
        const article = data?.data || data;
        if (!article || !article.id) {
          setError("Article not found.");
          setTutorial(null);
          return;
        }
        let attachments = article.attachments;
        if (typeof attachments === "string") {
          try {
            attachments = JSON.parse(attachments);
          } catch {
            attachments = [];
          }
        }
        setTutorial({ ...article, attachments: attachments || [] });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Failed to load article.");
          setTutorial(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEdit, articleId]);

  const activeModuleSlug =
    tutorial?.moduleSlug || fixedModuleSlug || "";

  const activeModuleName = useMemo(() => {
    const match = modules.find((m) => m.slug === activeModuleSlug);
    return match?.moduleName || match?.name || activeModuleSlug;
  }, [modules, activeModuleSlug]);

  const goBack = () => {
    if (isEdit && activeModuleSlug && articleId) {
      navigate(`/knowledge-base/${activeModuleSlug}/${articleId}`);
      return;
    }
    if (fixedModuleSlug) {
      navigate(`/knowledge-base/${fixedModuleSlug}`);
      return;
    }
    navigate("/knowledge-base");
  };

  const breadcrumbs = [
    { label: "Knowledge Base", path: "/knowledge-base" },
    ...(activeModuleSlug
      ? [
          {
            label: activeModuleName || activeModuleSlug,
            path: `/knowledge-base/${activeModuleSlug}`,
          },
        ]
      : []),
    ...(isEdit && tutorial?.title
      ? [
          {
            label: tutorial.title,
            path: `/knowledge-base/${activeModuleSlug}/${articleId}`,
          },
        ]
      : []),
    { label: isEdit ? "Edit" : "Add" },
  ];

  if (loading) {
    return (
      <div className="kb-home kb-form-page-wrap">
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress size={36} sx={{ color: "#22419e" }} />
          <Typography sx={{ mt: 2, color: "#64748b", fontSize: 14 }}>
            Loading article…
          </Typography>
        </Box>
      </div>
    );
  }

  if (isEdit && (error || !tutorial)) {
    return (
      <div className="kb-home kb-form-page-wrap">
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ color: "#b91c1c", fontWeight: 600, mb: 2 }}>
            {error || "Article not found."}
          </Typography>
          <button type="button" className="fm-btn is-primary" onClick={goBack}>
            Go back
          </button>
        </Box>
      </div>
    );
  }

  return (
    <div id="kt_content_container" className="kb-home kb-form-page-wrap">
      <BreadcrumbsComponent breadcrumbs={breadcrumbs} />

      <div className="kb-page-head">
        <div className="kb-page-head-main">
          <IconButton
            className="kb-back-icon"
            onClick={goBack}
            aria-label="Back"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <div>
            <h1>{isEdit ? "Edit Knowledge Base" : "Add Knowledge Base"}</h1>
            <p>
              {isEdit
                ? "Update article details, video guide, and attachments."
                : "Create a new knowledge base entry to help your team."}
            </p>
          </div>
        </div>
      </div>

      <ArticleFormDialog
        asPage
        open
        tutorial={tutorial}
        modules={modules}
        fixedModuleSlug={fixedModuleSlug}
        fixedProductType=""
        onClose={goBack}
        onSaved={(saved) => {
          const slug =
            saved?.moduleSlug || tutorial?.moduleSlug || fixedModuleSlug;
          const id = saved?.id || articleId;
          const uploading =
            saved?._videoUploading ||
            saved?.uploadStatus === "pending" ||
            saved?.uploadStatus === "processing";
          // While video uploads in background, go to list (progress bar lives there)
          if (uploading && slug) {
            navigate(`/knowledge-base/${slug}`);
            return;
          }
          if (slug && id) {
            navigate(`/knowledge-base/${slug}/${id}`);
            return;
          }
          goBack();
        }}
      />
    </div>
  );
};

export default ArticleFormPage;
